import { useEffect, useState } from "react";
import { Popup } from "devextreme-react/popup";
import notify from "devextreme/ui/notify";
import { FiLock } from "react-icons/fi";
import {
  ExpedienteEstadoUi,
  ExpedienteGridRow,
} from "../../../dtos/CatCabeceraManifold";
import {
  loadManifoldEnvases,
  ManifoldEnvaseSerie,
} from "../../../utils/manifoldEnvases";
import * as S from "./ExpedienteDetalle.styles";

const estadoTone = (
  estado: ExpedienteEstadoUi,
): "warning" | "success" | "danger" => {
  if (estado === "Aprobado") return "success";
  if (estado === "Rechazado") return "danger";
  return "warning";
};

interface Props {
  visible: boolean;
  row: ExpedienteGridRow | null;
  /** "info" oculta Aprobar/Rechazar; "decision" los muestra si está pendiente. */
  mode?: "info" | "decision";
  deciding?: boolean;
  onClose: () => void;
  onAprobar: () => void;
  onRequestReject: () => void;
}

const ExpedienteDetallePopup: React.FC<Props> = ({
  visible,
  row,
  mode = "info",
  deciding = false,
  onClose,
  onAprobar,
  onRequestReject,
}) => {
  const [loadingEnvases, setLoadingEnvases] = useState(false);
  const [envases, setEnvases] = useState<ManifoldEnvaseSerie[]>([]);
  const [horaInicio, setHoraInicio] = useState("—");
  const [horaTermino, setHoraTermino] = useState("—");

  useEffect(() => {
    if (!visible || !row) {
      setEnvases([]);
      setHoraInicio("—");
      setHoraTermino("—");
      return;
    }

    let cancelled = false;

    const loadEnvases = async () => {
      setLoadingEnvases(true);
      try {
        const result = await loadManifoldEnvases(row.id_Manifold, {
          intCabecera: row.intCabecera,
          parada: row.parada,
          fechaLlenado: row.fechaLlenado,
        });
        if (!cancelled) {
          setEnvases(result.series);
          setHoraInicio(result.horaInicio);
          setHoraTermino(result.horaTermino);
        }
      } catch {
        if (!cancelled) {
          setEnvases([]);
          notify(
            "No se pudieron cargar los envases del manifold.",
            "warning",
            2500,
          );
        }
      } finally {
        if (!cancelled) setLoadingEnvases(false);
      }
    };

    loadEnvases();
    return () => {
      cancelled = true;
    };
  }, [visible, row]);

  if (!row) return null;

  const isPending = row.estadoUi === "Pendiente de aprobar";
  const isRejected = row.estadoUi === "Rechazado";
  const isApproved = row.estadoUi === "Aprobado";
  const total = row.totalEnvases || envases.length;

  const copySerie = async (serie: string) => {
    try {
      await navigator.clipboard.writeText(serie);
      notify(`Serie ${serie} copiada.`, "success", 1500);
    } catch {
      notify(serie, "info", 2000);
    }
  };

  return (
    <Popup
      visible={visible}
      onHiding={() => {
        if (!deciding) onClose();
      }}
      dragEnabled={false}
      showCloseButton={false}
      showTitle={false}
      width={460}
      maxWidth="95vw"
      height="auto"
      maxHeight="90vh"
      className="custom-form-popup"
    >
        <S.Card>
          <S.CloseButtonIcon
            type="button"
            title="Cerrar"
            aria-label="Cerrar"
            disabled={deciding}
            onClick={onClose}
          >
            ×
          </S.CloseButtonIcon>
          <S.HeaderRow>
            <S.ManifoldTitle>{row.manifoldNombre}</S.ManifoldTitle>
            <S.StatusLabel $tone={estadoTone(row.estadoUi)}>
              {row.estadoUi}
            </S.StatusLabel>
          </S.HeaderRow>

          <S.Details>
            <S.DetailRow>
              <S.DetailLabel>Sucursal: </S.DetailLabel>
              <S.DetailValue>{row.sucursalLabel}</S.DetailValue>
            </S.DetailRow>
            <S.DetailRow>
              <S.DetailLabel>Producto: </S.DetailLabel>
              <S.DetailValue>{row.productoNombre}</S.DetailValue>
            </S.DetailRow>

            <S.InlineGroup>
              <S.DetailRow>
                <S.DetailLabel>Línea: </S.DetailLabel>
                <S.DetailValue>{row.linea || "—"}</S.DetailValue>
              </S.DetailRow>
              <S.DetailRow>
                <S.DetailLabel>Parada: </S.DetailLabel>
                <S.DetailValue>{row.parada || "—"}</S.DetailValue>
              </S.DetailRow>
              <S.DetailRow>
                <S.DetailLabel>Turno: </S.DetailLabel>
                <S.DetailValue>{row.turno || "—"}</S.DetailValue>
              </S.DetailRow>
            </S.InlineGroup>

            <S.DetailRow>
              <S.DetailLabel>Operador: </S.DetailLabel>
              <S.DetailValue>{row.operadorNombre}</S.DetailValue>
            </S.DetailRow>

            {(isApproved || isRejected) && (
              <S.DetailRow>
                <S.DetailLabel>Supervisor: </S.DetailLabel>
                <S.DetailValue>{row.supervisorNombre}</S.DetailValue>
              </S.DetailRow>
            )}

            <S.DetailRow>
              <S.DetailLabel>Número de envases: </S.DetailLabel>
              <S.DetailValue>{total}</S.DetailValue>
            </S.DetailRow>

            <S.InlinePairGroup>
              <S.DetailRow>
                <S.DetailLabel>Hora inicio: </S.DetailLabel>
                <S.DetailValue>{horaInicio}</S.DetailValue>
              </S.DetailRow>
              <S.DetailRow>
                <S.DetailLabel>Hora término: </S.DetailLabel>
                <S.DetailValue>{horaTermino}</S.DetailValue>
              </S.DetailRow>
            </S.InlinePairGroup>
          </S.Details>

          {isRejected && row.motivo && (
            <S.MotivoBox>
              <S.MotivoTitle>Motivo de rechazo</S.MotivoTitle>
              <S.MotivoText>{row.motivo}</S.MotivoText>
            </S.MotivoBox>
          )}

          <S.EnvasesSection>
            <S.EnvasesHeading>Envases</S.EnvasesHeading>
            {loadingEnvases ? (
              <S.LoadingBox>Cargando envases...</S.LoadingBox>
            ) : envases.length === 0 ? (
              <S.LoadingBox>Sin envases registrados.</S.LoadingBox>
            ) : (
              <>
                <S.EnvasesScroll>
                  <S.EnvasesGrid>
                    {envases.map((e) => (
                      <S.EnvaseLink
                        key={e.id}
                        type="button"
                        title="Copiar número de serie"
                        onClick={() => copySerie(e.numeroSerie)}
                      >
                        {e.numeroSerie}
                      </S.EnvaseLink>
                    ))}
                  </S.EnvasesGrid>
                </S.EnvasesScroll>
                {envases.length > 16 && (
                  <S.EnvasesHint>
                    Desplaza hacia abajo para ver los {total} envases
                  </S.EnvasesHint>
                )}
                <S.EnvasesTotal>{total} envases en total</S.EnvasesTotal>
              </>
            )}
          </S.EnvasesSection>

          {isPending && mode === "decision" && (
            <S.ActionsRow>
              <S.ApproveButton
                type="button"
                disabled={deciding}
                onClick={onAprobar}
              >
                {deciding ? "Procesando..." : "Aprobar"}
              </S.ApproveButton>
              <S.RejectButton
                type="button"
                disabled={deciding}
                onClick={onRequestReject}
              >
                Rechazar
              </S.RejectButton>
            </S.ActionsRow>
          )}

          {isPending && mode === "info" && (
            <S.CloseButton type="button" onClick={onClose}>
              Cerrar
            </S.CloseButton>
          )}

          {isApproved && (
            <>
              <S.LockBanner $variant="approved">
                <FiLock />
                Registro aprobado. No editable.
              </S.LockBanner>
              <S.CloseButton type="button" onClick={onClose}>
                Cerrar
              </S.CloseButton>
            </>
          )}

          {isRejected && (
            <>
              <S.LockBanner $variant="rejected">
                <FiLock />
                Registro rechazado. No editable. Sin PDF disponible.
              </S.LockBanner>
              <S.CloseButton type="button" onClick={onClose}>
                Cerrar
              </S.CloseButton>
            </>
          )}
        </S.Card>
    </Popup>
  );
};

export default ExpedienteDetallePopup;
